import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import AddressAPI from '../Api/address';
import countries from '../Constants/countries';
import { Suggestion } from '../../Components/ReusableComponents/AutoCompleteAddress/types';

const useGetAddressByPlaceId = () =>
  useMutation(
    (placeId: string) => AddressAPI.getAddressByPlaceId({ placeId }),
    {
      onError: () => {
        toast('Error while verifying the address', {
          type: 'error',
        });
      },
      onSuccess: () => {},
    },
  );

type UseGetAddressSuggestionsParams = {
  value: string;
  fieldInputValue: string;
  country: string;
};

function useGetAddressSuggestions({
  value,
  fieldInputValue,
  country,
}: UseGetAddressSuggestionsParams) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<(string | Suggestion)[]>([]);

  const fetch = React.useMemo(() => {
    const countryAlpha2 = countries.find((c) => c.value === country)?.value;
    return debounce(async (request, callback) => {
      setIsLoading(true);
      try {
        const res = await AddressAPI.getAddressSuggestions({
          ...request,
          country: countryAlpha2,
        });
        callback(res);
      } catch (err) {
        toast('Error fetching address suggestions', { type: 'error' });
        console.error('Error fetching address suggestions', err);
      }
    }, 300);
  }, [country]);

  useEffect(() => {
    let active = true;
    if (fieldInputValue === '') {
      setSuggestions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        search: fieldInputValue,
      },
      (results: Suggestion[]) => {
        setIsLoading(false);
        if (active) {
          let newSuggestions: (string | Suggestion)[] = [];
          if (value) {
            newSuggestions = [value];
          }

          if (results) {
            newSuggestions = [...newSuggestions, ...results];
          }

          setSuggestions(newSuggestions);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [value, fieldInputValue, fetch]);

  return { isLoading, suggestions, setSuggestions };
}

export { useGetAddressByPlaceId, useGetAddressSuggestions };
